class VQProductTabs extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.tabs = Array.from(this.querySelectorAll('[data-tab-trigger]'));
    this.panels = Array.from(this.querySelectorAll('[data-tab-panel]'));

    if (!this.tabs.length || !this.panels.length) return;

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    const initialIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');

    if (initialIndex >= 0) {
      this.activate(initialIndex, { focus: false });
    } else {
      this.resetInactiveState();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  onClick(event) {
    const tab = event.target.closest('[data-tab-trigger]');
    if (!tab || !this.contains(tab)) return;

    const index = this.tabs.indexOf(tab);
    if (index < 0) return;

    this.activate(index, { focus: true });
  }

  onKeydown(event) {
    const tab = event.target.closest('[data-tab-trigger]');
    if (!tab || !this.contains(tab)) return;

    const currentIndex = this.tabs.indexOf(tab);
    if (currentIndex < 0) return;

    let nextIndex = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'Right':
        nextIndex = (currentIndex + 1) % this.tabs.length;
        break;
      case 'ArrowLeft':
      case 'Left':
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.tabs.length - 1;
        break;
      case 'Enter':
      case ' ':
        nextIndex = currentIndex;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.activate(nextIndex, { focus: true });
  }

  onBlockSelect(event) {
    const target = event.target.closest('[data-block-id]');
    if (!target || !this.contains(target)) return;

    const blockId = target.dataset.blockId;
    const index = this.tabs.findIndex((tab) => tab.dataset.blockId === blockId);

    if (index < 0) return;

    this.activate(index, { focus: false });
  }

  resetInactiveState() {
    this.tabs.forEach((tab, index) => {
      tab.setAttribute('aria-selected', 'false');
      tab.setAttribute('tabindex', index === 0 ? '0' : '-1');
    });

    this.panels.forEach((panel) => {
      panel.hidden = true;
    });
  }

  activate(index, { focus = false } = {}) {
    this.tabs.forEach((tab, tabIndex) => {
      const isActive = tabIndex === index;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');

      if (focus && isActive) {
        tab.focus();
      }
    });

    this.panels.forEach((panel, panelIndex) => {
      panel.hidden = panelIndex !== index;
    });
  }

  activateByTarget(target, { focus = false } = {}) {
    if (!target) return false;

    const index = this.tabs.findIndex((tab) => tab.dataset.tabTarget === target);

    if (index < 0) return false;

    this.activate(index, { focus });
    return true;
  }
}

if (!customElements.get('vq-product-tabs')) {
  customElements.define('vq-product-tabs', VQProductTabs);
}

function getStickyHeaderOffset() {
  const styles = getComputedStyle(document.body);
  const offsets = [
    styles.getPropertyValue('--vq-header-height'),
    styles.getPropertyValue('--vq-header-group-height'),
    styles.getPropertyValue('--header-group-height'),
    styles.getPropertyValue('--header-height'),
  ];

  for (const offset of offsets) {
    const parsed = parseFloat(offset);
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
  }

  return 0;
}

function scrollElementIntoViewWithOffset(element, extraOffset = 20) {
  if (!element) return;

  const stickyHeaderOffset = getStickyHeaderOffset();
  const top = element.getBoundingClientRect().top + window.scrollY - stickyHeaderOffset - extraOffset;

  window.scrollTo({
    top: Math.max(top, 0),
    behavior: 'smooth',
  });
}

function scrollToProductTabTarget(target) {
  if (!target) return;

  const section = document.querySelector('.vq-product-tabs-section');
  if (!section) return;

  const tabsElement = section.querySelector('vq-product-tabs');
  const desktopTab = section.querySelector(`.vq-product-tabs-section__tab[data-tab-target="${target}"]`);
  const mobileItem = section.querySelector(`.vq-product-tabs-section__mobile-item[data-tab-target="${target}"]`);
  const mobileSummary = mobileItem?.querySelector('.vq-product-tabs-section__mobile-summary');
  const isMobile = window.matchMedia('(max-width: 749px)').matches;

  if (isMobile && mobileItem) {
    mobileItem.open = true;
    scrollElementIntoViewWithOffset(mobileSummary || mobileItem, 20);
    return;
  }

  if (tabsElement?.activateByTarget(target, { focus: false })) {
    scrollElementIntoViewWithOffset(desktopTab || section, 20);
  }
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-product-tab-link]');
  if (!trigger) return;

  event.preventDefault();
  scrollToProductTabTarget(trigger.dataset.productTabTarget);
});
